import { Injectable, inject } from '@angular/core';
import {FinancialService} from './api/fit-api/financial.service';
import {FundType} from './api/fit-api/models/funds/fund-type';
import {combineLatest, map, of} from 'rxjs';
import {DatasetDetail} from './api/fit-api/models/datasets/dataset-type';
import {LoggerService} from './logger.service';
import {removeDuplicates} from './functions/array/remove-duplicates';
import {LocalGovernment} from './api/fit-api/models/local-government';
import {ALL_FUNDS, GarfieldFundNode} from '../components/garfield-funds-selector/garfield-fund-node-model';
import {FundCategory} from "./api/fit-api/models/funds/fund-category";
import { FundField } from '../components/funds-selector/fund-node-model';

@Injectable({
	providedIn: 'root'
})
export class FundsService {
	private financialService = inject(FinancialService);
	private logger = inject(LoggerService);


	/**
	 * Return a list of filed fund nodes (filed funds based on a list of gov ids and their datasets)
	 *
	 * @param governments
	 * @param datasetsDetail
	 */
	getFiledFundNodes(governments: LocalGovernment[], datasetsDetail: DatasetDetail[]) {
		if (datasetsDetail.length === 0) {
			return of(undefined)
		}

		// iterate over datasets and create an object with the correct dataset route (schools or snapshot) and an array of gov ids for that dataset
		const filedFunds = datasetsDetail.map((data: DatasetDetail) => {
			const govIds = governments.filter(gov => gov.financialsDatasetSource === data.source).map(gov => gov.governmentId);
			return this.financialService.getFiledFundsForGovList(data.route, govIds);
		});

		return combineLatest(filedFunds).pipe(map((fundTypes: (FundType[] | undefined)[] ) => {
			// flatten array and remove duplicates
			const flatFiledFunds = removeDuplicates(fundTypes.flat().filter(ft => ft !== undefined), 'id');

			// map to FundNode
			return this.mapFiledFundsToFundNode(flatFiledFunds);
		}));
	}

	mapFiledFundsToFundNode(filedFunds: FundType[]) {
		return filedFunds.map(fundType => new GarfieldFundNode(fundType.categoryId, fundType.id, null))
	}

	/**
	 * Generate fund node filter object from given pivot cell (used by data cell navigation)
	 *
	 * - Example return: {fieldName: fundTypeId, label: Debt Service, id: 2}
	 * - todo refactor to use one pattern when new report pattern is implemented in financial report page
	 * - todo either type or remove anonymous type from return
	 * @param hierarchyValues
	 * @param hierarchyFieldNames
	 * @param fundCategories
	 * @param fundTypes
	 * @param selectedFundNode
	 */
	static generateFundNodeFilter =
		(
			hierarchyValues: (string | number)[],
			hierarchyFieldNames: string[],
			fundCategories: FundCategory[],
			fundTypes: FundType[],
			selectedFundNode: GarfieldFundNode | string | number
		) => {
			let selectedFundNodeFieldName;
			let selectedFundNodeName;
			let selectedFundNodeId;

			if (selectedFundNode instanceof GarfieldFundNode) {
				if (selectedFundNode.id === ALL_FUNDS) {
					selectedFundNodeFieldName = FundField.allFunds;
					selectedFundNodeName = ALL_FUNDS;
					selectedFundNodeId = ALL_FUNDS;
				} else if (selectedFundNode.fundCategoryId !== null && selectedFundNode.fundTypeId === null) {
					selectedFundNodeFieldName = FundField.fundCategoryId;
					const selectedFundCategory = fundCategories.find(c => c.id === selectedFundNode.fundCategoryId);
					selectedFundNodeName = selectedFundCategory?.name;
					selectedFundNodeId = selectedFundCategory?.id;
				} else if (selectedFundNode.fundCategoryId !== null && selectedFundNode.fundTypeId !== null) {
					selectedFundNodeFieldName = FundField.fundTypeId;
					const selectedFundType = fundTypes.find(c => c.id === selectedFundNode.fundTypeId);
					selectedFundNodeName = selectedFundType?.name;
					selectedFundNodeId = selectedFundType?.id;
				} else {
					selectedFundNodeFieldName = FundField.allFunds;
					selectedFundNodeName = ALL_FUNDS;
					selectedFundNodeId = ALL_FUNDS;
				}
			} else {
				selectedFundNodeFieldName = hierarchyFieldNames[hierarchyValues.length - 1];
				if (selectedFundNodeFieldName === FundField.allFunds) {
					selectedFundNodeName = ALL_FUNDS;
					selectedFundNodeId = ALL_FUNDS;
				} else if (selectedFundNodeFieldName === FundField.fundCategoryId) {
					const selectedFundCategory = fundCategories.find(c => c.id === selectedFundNode);
					selectedFundNodeName = selectedFundCategory?.name;
					selectedFundNodeId = selectedFundCategory?.id;
				} else if (selectedFundNodeFieldName === FundField.fundTypeId) {
					const selectedFundType = fundTypes.find(c => c.id === selectedFundNode);
					selectedFundNodeName = selectedFundType?.name;
					selectedFundNodeId = selectedFundType?.id;
				} else {
					// note: case where last column value doesn't equal all funds, fund category, or fund types
					// e.g. years that have a missing filing reason or no filing
					selectedFundNodeFieldName = FundField.allFunds;
					selectedFundNodeName = ALL_FUNDS;
					selectedFundNodeId = ALL_FUNDS;
				}
			}

			if (selectedFundNodeName === undefined || selectedFundNodeId === undefined)  {
				throw new Error('selectedFundNodeFieldName or selectedFundNodeId is undefined');
			}

			return {fieldName: selectedFundNodeFieldName, label: selectedFundNodeName, id: selectedFundNodeId?.toString()};
		}

}
